import { keyframes, style } from '@vanilla-extract/css'
import { vars } from '~/styles/theme.css'
import { formClassLarge } from '../Form.css'

export const wrapper = style({
  color: vars.color.dark,
  fontSize: vars.font.body.size,
  fontWeight: vars.font.body.weight,
  position: 'relative',
  minWidth: 0
})

export const dropDownValueBox = style({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  minHeight: 48,
  borderRadius: vars.buttons.radius,
  backgroundColor: vars.color.mediumLighter,
  border: '2px solid transparent',
  gap: 8,
  transition: 'all 0.3s',

  ':hover': {
    borderColor: vars.color.darkLighter
  },

  selectors: {
    [`${formClassLarge} &`]: {
      backgroundColor: vars.color.drawBackgroundLighter,
      border: `1px solid ${vars.color.mainHover}`
    }
  }
})

export const dropDownValueBoxExtraSmall = style({
  padding: '2px 8px',
  fontSize: '0.9em'
})

export const valueBoxNarrow = style({
  borderColor: 'transparent',
  padding: 4,
  gap: 4,
  selectors: {
    [`${formClassLarge} &`]: {
      borderColor: 'transparent'
    }
  }
})

export const valueBoxAsMenu = style({
  borderColor: 'transparent'
})

export const valueBoxGhost = style({
  selectors: {
    ':not(:hover) &': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      gap: 0,
      padding: 0
    }
  }
})

const shimmer = keyframes({
  '0%': {
    backgroundPosition: '-200% 0'
  },
  '100%': {
    backgroundPosition: '200% 0'
  }
})

export const valueBoxUpdating = style({
  backgroundColor: vars.color.light,
  cursor: 'progress',
  borderColor: 'transparent',
  border: '2px solid transparent',
  position: 'relative',
  overflow: 'visible',
  zIndex: 2,
  opacity: 0.5,
  background:
    'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.15), transparent)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 3s linear infinite`,

  ':hover': {
    borderColor: 'transparent'
  }
})

export const valueBoxActive = style({
  borderColor: vars.color.darkLighter
})

export const valueBoxDisabled = style({
  backgroundColor: vars.color.mediumLighter,
  cursor: 'not-allowed',
  opacity: 0.7,
  ':hover': {
    borderColor: 'transparent'
  }
})

export const selectedValue = style({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: 0
})

export const selectedValueText = style({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
})

export const selectedValueInner = style({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  gap: 4
})

export const selectedValueTextParent = style({
  fontSize: '0.7em',
  color: vars.color.darkLighter,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
})

export const openIcon = style({
  marginLeft: 'auto',
  marginTop: -4,
  transition: 'transform 0.3s',
  color: vars.color.darkLighter
})

export const openIconOpen = style({
  transform: 'rotate(180deg)'
})

export const openIconGhost = style({
  maxWidth: 0,
  overflow: 'hidden',
  opacity: 0,
  marginLeft: 0,
  transition: 'max-width 0.2s ease, opacity 0.2s ease',
  selectors: {
    [`${valueBoxGhost}:hover &`]: {
      maxWidth: '2em',
      opacity: 1,
      marginLeft: 'auto'
    },
    [`${valueBoxActive} &`]: { maxWidth: '2em', opacity: 1, marginLeft: 'auto' }
  }
})

export const optionsPortal = style({
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column'
})

export const optionsWrapper = style({
  backgroundColor: vars.color.mediumLighter,
  borderRadius: vars.buttons.radius,
  boxShadow: vars.shadow,
  color: vars.color.dark,
  fontSize: vars.font.body.size,
  fontWeight: vars.font.body.weight,
  maxWidth: '50vw',
  padding: '8px 16px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
})

export const optionsWrapperInline = style({
  boxShadow: 'none',
  maxWidth: '100%',
  padding: '4px 0',
  backgroundColor: 'transparent'
})

export const optionsList = style({
  maxHeight: 300,
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  scrollbarColor: `${vars.color.medium} transparent`,
  paddingBottom: 5
})

export const searchInputWrapper = style({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: vars.color.white,
  padding: '5px 8px',
  gap: 8,
  borderRadius: vars.buttons.radius,
  marginBottom: 8,
  top: 0,
  zIndex: 1000
})

export const searchInput = style({
  border: 'none',
  width: '100%',
  flex: 1,
  outline: 'none',
  color: vars.color.dark,
  fontSize: vars.font.body.size,
  fontWeight: vars.font.body.weight,
  height: '1.6em',

  selectors: {
    // Override v2 legacy style
    '&:is([type=text])': {
      height: '1.6em',
      margin: 0,
      padding: 0,
      float: 'none',
      border: 'none',
      width: '100%',
      fontSize: vars.font.body.size,
      fontWeight: vars.font.body.weight
    }
  }
})

export const optionItemNotSelectable = style({
  color: vars.color.darkLighter
})

export const optionItemGroup = style({
  padding: '10px 8px 4px',
  fontSize: vars.font.smallNormal.size,
  fontWeight: 600,
  color: vars.color.darkLighter,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  cursor: 'default',
  userSelect: 'none',

  selectors: {
    '&:not(:first-child)': {
      marginTop: 4,
      borderTop: `1px solid ${vars.color.medium}`
    }
  }
})

export const optionItem = style({
  padding: 8,
  cursor: 'pointer',
  transition: 'background-color 0.3s',
  borderRadius: vars.buttons.radius,
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  selectors: {
    [`&:not(.${optionItemNotSelectable}):hover`]: {
      backgroundColor: vars.color.drawBackgroundLighter
    },
    [`&.${optionItemNotSelectable}`]: {
      cursor: 'default'
    }
  }
})

export const optionItemActive = style({
  backgroundColor: vars.color.drawBackgroundLighter
})

export const optionItemLabel = style({
  flexGrow: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0
})

export const optionItemElementLabel = style({
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  gap: 2
})

export const optionItemElementLabelMain = style({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
})

export const optionItemElementSublabel = style({
  textAlign: 'left',
  fontSize: vars.font.smallNormal.size,
  color: vars.color.mediumDarker,
  fontWeight: vars.font.smallNormal.weight,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
})

export const multiSelectContainer = style({
  display: 'flex',
  flexWrap: 'wrap',
  height: 'auto',
  minWidth: 0,
  gap: '5px',
  flex: 1
})

export const selectedTag = style({
  backgroundColor: vars.color.drawBackgroundLighter,
  padding: '4px 8px',
  borderRadius: 3,
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,

  selectors: {
    [`${formClassLarge} &`]: {
      backgroundColor: vars.color.white
    }
  }
})

export const removeTagIcon = style({
  marginLeft: 5,
  cursor: 'pointer',
  color: vars.color.medium,
  opacity: 0.5,
  transition: 'opacity 0.15s, color 0.15s',

  ':hover': {
    color: vars.color.dark
  },

  selectors: {
    [`${selectedTag}:hover &`]: {
      opacity: 1
    },
    [`${selectedValue}:hover &`]: {
      opacity: 1
    }
  }
})

export const checkmark = style({
  marginLeft: 8,
  color: vars.color.darkLighter
})

export const optionItemChevron = style({
  marginRight: 8,
  transition: 'transform 0.2s ease',
  cursor: 'pointer',
  color: vars.color.darkLighter,
  fontSize: '0.8em',

  selectors: {
    '&.expanded': {
      transform: 'rotate(180deg)'
    }
  }
})

export const optionItemChildren = style({
  paddingLeft: 16,
  marginLeft: 16,
  borderLeft: `1px solid ${vars.color.medium}`
})

export const subOptionsContainer = style({
  fontSize: '0.85em',
  color: vars.color.darkLighter
})

export const subOptionsToggle = style({
  cursor: 'pointer',
  fontSize: '0.8em',
  color: vars.color.darkLighter,
  marginLeft: 8,
  transition: 'transform 0.2s ease',

  selectors: {
    '&.expanded': {
      transform: 'rotate(180deg)'
    }
  }
})

export const subOptionItem = style({
  padding: '4px 8px',
  fontSize: '0.85em',
  borderRadius: vars.buttons.radius,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  transition: 'background-color 0.2s',

  ':hover': {
    backgroundColor: vars.color.drawBackgroundLighter
  }
})
